import React from 'react';
import { MapPin, Phone, Clock, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const Contact = () => {
  const hours = [
    { day: 'Monday - Sunday', time: '9:00 AM - 9:00 PM' },
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '+91 99558 85574',
      link: 'tel:+919955885574',
    },
    {
      icon: MapPin,
      title: 'Address',
      content: 'No. 37, 1st floor, 10th Main Rd, near Ashoka Pillar Road, 1st Block, Jayanagar, Bengaluru, Karnataka 560011',
      link: 'https://maps.google.com/?cid=1419869882600592434&g_mp=CiVnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLkdldFBsYWNlEAIYBCAA',
    },
    {
      icon: Clock,
      title: 'Hours',
      content: 'Open 7 days a week',
      subContent: '9:00 AM - 9:00 PM',
    },
  ];

  return (
    <section id="contact" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Visit Us Today
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Located in the heart of Jayanagar. Walk-ins welcome, or call ahead to book your appointment.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="space-y-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow duration-300">
                  <CardHeader className="pb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-accent-primary rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <CardTitle className="text-xl">{info.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {info.link ? (
                      <a
                        href={info.link}
                        target={info.icon === MapPin ? '_blank' : undefined}
                        rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                        className="text-gray-700 hover:text-accent-primary transition-colors duration-200"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <div>
                        <p className="text-gray-700">{info.content}</p>
                        {info.subContent && (
                          <p className="text-accent-primary font-semibold mt-1">
                            {info.subContent}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {/* Opening Hours Card */}
            <Card className="border-none shadow-md bg-gradient-to-br from-accent-primary to-accent-primary-dark text-white">
              <CardHeader>
                <CardTitle className="text-white">Opening Hours</CardTitle>
              </CardHeader>
              <CardContent>
                {hours.map((schedule, index) => (
                  <div key={index} className="flex justify-between items-center py-2">
                    <span className="font-medium">{schedule.day}</span>
                    <span className="font-bold">{schedule.time}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Google Maps Embed */}
          <div className="h-full min-h-[500px]">
            <Card className="border-none shadow-lg h-full overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.9086!2d77.5835!3d12.9275!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae15d5a2d8b4d7%3A0x13b3f8c3c3c3c3c3!2sEnvoq%20Salon%20-%20Jayanagar!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Envoq Salon Location"
                className="rounded-lg"
              />
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-gradient-to-br from-[#f7f5f2] to-white rounded-2xl p-8 md:p-12 shadow-lg">
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready for Your Transformation?
          </h3>
          <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
            Book your appointment today and experience the Envoq difference. Our expert team is ready to make you look and feel your best.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+919955885574"
              className="inline-flex items-center justify-center bg-accent-primary hover:bg-accent-primary-dark text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:shadow-xl text-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call +91 99558 85574
            </a>
            <a
              href="https://maps.google.com/?cid=1419869882600592434&g_mp=CiVnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLkdldFBsYWNlEAIYBCAA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-white hover:bg-gray-50 text-accent-primary font-semibold px-8 py-4 rounded-lg border-2 border-accent-primary transition-all duration-300 hover:shadow-xl text-lg"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Get Directions
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;